import { SidebarAccordion } from '@/components/ui/Accordion'
import { SidebarFilterChip } from '@/components/ui/Chip'
import { Box, GridLegacy as Grid, Typography } from '@mui/material'

import React, { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { MenuItemFilter, MenuItemFilterSection } from '../../domain/menu-item-filter'
import { getTruncatedLabel } from './CatergoryFilter'

type Props = {
  type: MenuItemFilterSection
  label: string
  filters: MenuItemFilter[]
  selectedFilters: MenuItemFilter[]
  visibility: boolean
  setDietaryPreferences: (type: MenuItemFilterSection, filters: MenuItemFilter[]) => void
}

type FilterItemProps = {
  filter: MenuItemFilter
  isSelected: boolean
  filterLabel: string
  onChipClick: (filter: MenuItemFilter) => void
}

const FilterItem = memo(
  ({ filter, isSelected, filterLabel, onChipClick }: FilterItemProps) => {
    const handleClick = useCallback(() => {
      onChipClick(filter)
    }, [onChipClick, filter])

    return (
      <Grid item md={12}>
        <SidebarFilterChip
          selected={isSelected}
          onClick={handleClick}
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body3" fontWeight={500}>
                {getTruncatedLabel(filterLabel)} ({filter.filteredCount})
              </Typography>
            </Box>
          }
        />
      </Grid>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.filter.value === nextProps.filter.value &&
      prevProps.filter.filteredCount === nextProps.filter.filteredCount &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.filterLabel === nextProps.filterLabel &&
      prevProps.onChipClick === nextProps.onChipClick
    )
  },
)

FilterItem.displayName = 'FilterItem'

const FilterSection: React.FC<Props> = memo(
  ({ type, label, filters, selectedFilters, visibility, setDietaryPreferences }) => {
    const { t } = useTranslation()

    const emptyFilter = useMemo(() => filters.every((filter) => filter.count === 0), [filters])

    const handleChipClick = useCallback(
      (filter: MenuItemFilter) => {
        const selected = selectedFilters.find((f) => f.value === filter.value)
        const newFilters = selected
          ? selectedFilters.filter((f) => f.value !== filter.value)
          : [...selectedFilters, filter]
        setDietaryPreferences(type, newFilters)
      },
      [selectedFilters, setDietaryPreferences, type],
    )

    const visibleFilters = useMemo(() => filters.filter((filter) => filter.count >= 1), [filters])

    const selectedValuesSet = useMemo(
      () => new Set(selectedFilters.map((f) => f.value)),
      [selectedFilters],
    )

    if (!visibility || emptyFilter) return null

    return (
      <SidebarAccordion label={label}>
        <Grid container spacing={1}>
          {visibleFilters.map((filter) => {
            const isSelected = selectedValuesSet.has(filter.value)
            const translated = t(`allergenDietary:${filter.value}`)
            const negate = filter.type === 'allergen' ? `${t('common:negate')} ` : ''
            const filterLabel =
              translated !== filter.value ? `${negate}${translated}` : filter.label

            return (
              <FilterItem
                key={filter.label}
                filter={filter}
                isSelected={isSelected}
                filterLabel={filterLabel}
                onChipClick={handleChipClick}
              />
            )
          })}
        </Grid>
      </SidebarAccordion>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.type === nextProps.type &&
      prevProps.label === nextProps.label &&
      prevProps.visibility === nextProps.visibility &&
      prevProps.filters === nextProps.filters &&
      prevProps.selectedFilters === nextProps.selectedFilters &&
      prevProps.setDietaryPreferences === nextProps.setDietaryPreferences
    )
  },
)

FilterSection.displayName = 'FilterSection'

export default FilterSection
